import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { noClientsDemoI18n } from '../i18n';
import { isVentaCall } from '../callKind';
import '../../Protocols/protocolGeneralDetail.scss';
import './transcriptView.css';
import CallChat2 from '../../../components/Chat2/CallChat2';
import {
  getNoClientsDemoTranscript,
  getNoClientsDemoAudioUrl,
} from 'services/noClientsDemoService';

const NoClientTranscriptView = ({ DesignSystemComponents }) => {
  const { Card, Button, Input } = DesignSystemComponents || {};
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useScopedTranslation(noClientsDemoI18n);

  const [transcript, setTranscript] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [audioUrl, setAudioUrl] = useState(null);

  useEffect(() => {
    const controller = new AbortController();

    const loadTranscript = async () => {
      if (!id) {
        setError(t('nc_tv_invalidId'));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await getNoClientsDemoTranscript(
          id,
          controller.signal
        );

        setTranscript(response);
        setSearchTerm('');
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error loading transcript:', err);
          setError(t('nc_tv_error'));
          setTranscript(null);
        }
      } finally {
        setLoading(false);
      }
    };

    loadTranscript();
    return () => controller.abort();
  }, [id]);

  const handleLoadAudio = async () => {
    try {
      // Demo: el backend devuelve una URL firmada (presigned) reproducible inline,
      // no un blob. Se asigna directo al <audio src>.
      const url = await getNoClientsDemoAudioUrl(id);
      if (url) setAudioUrl(url);
    } catch (error) {
      console.error('Error loading audio:', error);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return t('nc_na');
    // callDateTime viene en UTC sin 'Z' -> forzamos UTC y mostramos en hora de Chile.
    const hasTz = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(dateString);
    const d = new Date(hasTz ? dateString : `${dateString}Z`);
    if (Number.isNaN(d.getTime())) return t('nc_na');
    return d.toLocaleString('es-CL', {
      timeZone: 'America/Santiago',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const transcriptText = transcript?.transcriptText?.trim() || '';
  const summaryText = transcript?.summary || '';
  const venta = isVentaCall(transcript || {}); // en preventa se oculta el Sentimiento

  const formattedTranscript = useMemo(() => {
    if (!transcript?.transcriptText) return '';

    const raw = transcript.transcriptText.trim();

    // Divide cada vez que aparece "Agente:" o "Cliente:"
    const parts = raw.split(/(?=Agente:|Cliente:)/g);

    let currentTime = 0;

    return parts
      .map((part) => {
        const clean = part.trim();
        if (!clean) return null;

        const isAgent = clean.startsWith('Agente:');
        const speaker = isAgent ? 'agent' : 'customer';

        const text = clean.replace(/^Agente:|^Cliente:/, '').trim();

        const start = currentTime;
        const end = currentTime + 5;
        currentTime += 6;

        return `${start}-${end}-${speaker}-${text};`;
      })
      .filter(Boolean)
      .join('');
  }, [transcript]);

  if (!Card) return null;

  return (
    <div className='container client-detail'>
      <div className='d-flex align-items-center gap-2 mb-2'>
        {Button ? (
          <Button
            variant='naked'
            iconStart='chevronLeft'
            label={t('nc_back')}
            size='sm'
            onClick={() => navigate(-1)}
          />
        ) : (
          <button
            type='button'
            className='btn btn-link p-0'
            onClick={() => navigate(-1)}
          >
            {t('nc_back')}
          </button>
        )}
      </div>

      <div className='row'>
        <div className='calldetail-container'>
          <div className='col-6 transcript-col'>
            <Card specialPadding='p-4'>
              <div className='mb-4'>
                <h2 className='fw-medium mb-1'>{t('nc_tv_callTitle')}</h2>
                <p className='caption font-light mb-0'>ID: {id}</p>
              </div>

              <div>
                {[
                  {
                    label: t('nc_tv_date'),
                    value: transcript?.callDateTime
                      ? formatDateTime(transcript.callDateTime)
                      : t('nc_na'),
                  },
                  {
                    label: t('nc_tv_owner'),
                    value: transcript?.ownerName || transcript?.agentName || '-',
                  },
                  {
                    label: t('nc_tv_callAgent'),
                    value: transcript?.callAgentName || '-',
                  },
                  {
                    label: t('nc_tv_agentId'),
                    value: transcript?.agentId || '-',
                  },
                  {
                    label: t('nc_tv_customerPhone'),
                    value: transcript?.telephone || '-',
                  },
                  ...(venta
                    ? [
                        {
                          label: t('nc_tv_feeling'),
                          value: transcript?.feeling || '-',
                        },
                      ]
                    : []),
                  {
                    label: t('nc_tv_operation'),
                    value: venta ? t('nc_tv_sale') : t('nc_tv_presale'),
                  },
                  {
                    label: t('nc_tv_type'),
                    value: transcript?.typeInteraction || '-',
                  },
                ].map((item) => (
                  <div
                    key={item.label}
                    className='d-flex justify-content-between mb-2'
                  >
                    <p className='caption fw-semibold'>{item.label}</p>
                    <p className='caption'>{item.value || t('nc_na')}</p>
                  </div>
                ))}
              </div>

              {summaryText && (
                <>
                  <hr className='line mt-4 mb-3' />
                  <h3 className='fw-bold mb-3'>{t('nc_tv_summary')}</h3>
                  <div className='content-break summary-break'>
                    <p className='body mb-0'>{summaryText}</p>
                  </div>
                </>
              )}
            </Card>
          </div>
          <div className='col-6'>
            <Card notPadding>
              <div className='px-4 pt-4'>
                <div className='d-flex justify-content-center mb-3'>
                  {audioUrl ? (
                    <audio controls className='w-100' src={audioUrl} />
                  ) : (
                    <Button
                      variant='primary'
                      label={t('nc_tv_downloadAudio')}
                      size='sm'
                      onClick={handleLoadAudio}
                    />
                  )}
                </div>
                <Input
                  icon='search'
                  name='searchAudio'
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t('nc_tv_searchPlaceholder')}
                  type='text'
                  value={searchTerm}
                />
              </div>
              <hr className='line my-0' />
              <div className='content-break chat-break transcript-chat-break'>
                {loading && (
                  <p className='caption color-gray-600 mb-0'>
                    {t('nc_tv_loading')}
                  </p>
                )}

                {error && !loading && (
                  <p className='caption text-danger mb-0'>{error}</p>
                )}

                {!loading && !error && !!transcriptText && (
                  <CallChat2
                    callData={{ audioText: formattedTranscript }}
                    searchQuery={searchTerm}
                  />
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NoClientTranscriptView;
